import R from "react"
import {NavigateFunction} from "react-router-dom"
import {z} from "zod"
import { audience_t, audiences_schema, brand_t, creative_schema_t, creatives_schema, custom_audience_t, custom_audiences_schema, overview_stats_schema, overview_stats_t, dashboard_overview_schema, dashboard_overview_t, dashboard_network_health_schema, dashboard_network_health_t, dashboard_top_performing_schema, dashboard_top_performing_t, all_plans_schema, user_subscription_schema, campaign_eligibility_schema } from "./resource/types"
export type { usage_tier_t, user_subscription_t, campaign_eligibility_t } from "./resource/types"

/******************************************************************************
 ** General types and type-checking utilities; broadly-used functions [
 ******************************************************************************/

export type Ok = true
export type Err = false
export const Ok : Ok = true as Ok
export const Err : Err = false as Err
export type result<r, e> = [Ok, r] | [Err, e]
export const unreachable = (x : never) : never => { throw new Error(`unexpected ${x}`) }
export type fields_with_type<desired_t, t> = keyof { [f in keyof t as t[f] extends desired_t ? f : never] : any }

export const set_difference = <t,>(xs : Set<t>, ys : Set<t>) : Set<t> => {
    const ret = new Set<t>()
    xs.forEach((x) => { if (!ys.has(x)) ret.add(x) })
    return ret
}
export const filter_map = <t, t2>(xs : t[], f : (x : t) => result<t2, undefined>) : t2[] => {
    const ret : t2[] = []
    for (const x of xs) {
        const [ok, v] = f(x)
        if (ok) ret.push(v)
    }
    return ret
}

export type writable_t<t> = { -readonly [field in keyof t] : t[field] }

export const random128hex = () : string => {
    const m16 = new Uint8Array(16)
    window.crypto.getRandomValues(m16)
    const str = Array.prototype.map.call(m16, x => x.toString(16).padStart(2, "0")).join("")
    return str
}

/******************************************************************************
 ** General types and type-checking utilities; broadly-used functions ]
 ******************************************************************************/

/******************************************************************************
 ** Definitions involving the state of the entire single-page browser program [
 ******************************************************************************/

export type program_state = {
    current : current_user_state
    login_request_counter : generation_counter
    rerender : () => void,
    token_store : (token : authn_token | null) => void,
    token_load : () => authn_token | null,
    current_time : () => number,
    ps_sleep : (delay : number) => Promise<void>,
    subscriptions : subscriptions,
}
type subscriptions = {
    generation_counter : generation_counter,
    react_components : Map<() => void, subscription_target>,
}
type subscription_target = "connected_networks" | "brands" | "anything"
export const Authenticating : "ps-authenticating" = "ps-authenticating"
export const Unauthenticated : "ps-unauthenticated" = "ps-unauthenticated"
export const Authenticated : "ps-authenticated" = "ps-authenticated"

export type authenticating_state = {
    tag : typeof Authenticating,
    login_request_counter_snapshot : generation_counter
}
export type unauthenticated_state = {
    tag : typeof Unauthenticated,
    last_failure : no_attempts_yet | last_attempt_failed,
}
export const No_attempts_yet : "no-attempts-yet" = "no-attempts-yet"
export const Last_attempt_failed : "last-attempt-failed" = "last-attempt-failed"
export type no_attempts_yet = { tag : typeof No_attempts_yet }
export type last_attempt_failed = { tag : typeof Last_attempt_failed, email_message : string, password_message : string, generic_reason : string }
export type user_state = {
    readonly tag : typeof Authenticated,
    readonly connected_networks : connected_network[] | undefined,
    connected_networks_last_update : number,
    connected_networks_current_fetch_task : { should_abort : boolean } | null,
    readonly brands : brand[] | undefined,
    brands_last_update : number,
    brands_current_fetch_task : { should_abort : boolean } | null,
    readonly token : authn_token,
    readonly saved_audiences : audience_t[] | undefined,
    saved_audiences_last_update : number,
    saved_audiences_current_fetch_task : { should_abort : boolean } | null,
    readonly creatives : creative_schema_t[] | undefined,
    creatives_last_update : number,
    creatives_current_fetch_task : { should_abort : boolean } | null,
    custom_audiences : custom_audience_t[] | undefined,
    custom_audiences_last_update : number,
    custom_audiences_current_fetch_task : { should_abort : boolean } | null,
    overview_stats : overview_stats_t | undefined,
    overview_stats_last_update : number,
    overview_stats_current_fetch_task : { should_abort : boolean } | null,
    crapholder : crapholder,
    navbar_is_collapsed : boolean,
}
export type crapholder = Map<string, any>
export type current_user_state = unauthenticated_state | authenticating_state | user_state
export type program_state_box = { program_state : program_state }
export type generation_counter = number & { readonly __tag: unique symbol };
export const generation_counter_init = Number.MIN_SAFE_INTEGER as generation_counter
export const generation_counter_incr = (counter : generation_counter) : generation_counter => {
    const _counter = counter as number
    const next = _counter === Number.MAX_SAFE_INTEGER ? Number.MIN_SAFE_INTEGER : _counter + 1
    return next as generation_counter
}
export const mk_empty_user_state = (token : authn_token) : user_state => {
    return {
        tag : Authenticated,
        token : token,
        connected_networks : undefined,
        connected_networks_last_update : 0,
        connected_networks_current_fetch_task : null,
        brands: undefined,
        brands_last_update: 0,
        brands_current_fetch_task: null,
        saved_audiences: undefined,
        saved_audiences_last_update: 0,
        saved_audiences_current_fetch_task: null,
        creatives: undefined,
        creatives_last_update: 0,
        creatives_current_fetch_task: null,
        custom_audiences: undefined,
        custom_audiences_last_update: 0,
        custom_audiences_current_fetch_task: null,
        overview_stats: undefined,
        overview_stats_last_update: 0,
        overview_stats_current_fetch_task: null,
        crapholder : new Map(),
        navbar_is_collapsed : false,
    }
}
export type authn_token = { authn_token : string, readonly __newtype_authn_token : unique symbol }
export const authn_token_of_string = (s : string) : authn_token => s as unknown as authn_token

export type connected_network_tag = "nw-facebook" | "nw-linkedin" | "nw-tiktok" | "nw-x"

export type eres_brand_dbid = { readonly __newtype_eres_brand_dbid : unique symbol };
export type connected_network_dbid = string & { readonly __tag : unique symbol };
export type network_account_foreign_id = string & { readonly __tag : unique symbol };
export const cnet_dbid = (x : string) : connected_network_dbid => x as connected_network_dbid
export const net_fid = (x : string) : network_account_foreign_id => x as network_account_foreign_id
export const eres_brand_dbid_from_number = (raw_dbid : number) : eres_brand_dbid => raw_dbid as unknown as eres_brand_dbid
export const parse_eres_brand_dbid = (raw : string) : result<eres_brand_dbid, undefined> => {
    const parsed_num = parseInt(raw, 10)
    if (Number.isNaN(parsed_num)) return [Err, undefined]
    return [Ok, eres_brand_dbid_from_number(parsed_num)]
}

export type connected_network = {
    network_tag : connected_network_tag,
    name : string,
    network_account_id : network_account_foreign_id,
    eres_dbid : connected_network_dbid,
    creation_time : number,
}

const brand_fields = z.object({
    name : z.string(),
    url : z.string().nullish(),
    logo : z.string().nullish(),
    meta_page : z.number().nullish(),
    ad_account_id : z.number().nullish(),
    meta_pixel_id : z.number().nullish(),
    instagram_asset_id : z.number().nullish(),
    linkedin_org : z.number().nullish(),
    linkedin_ad_account : z.number().nullish(),
    tiktok_user_id : z.number().nullish(),
    tiktok_identity_id : z.number().nullish(),
    tiktok_ad_account_id : z.number().nullish(),
    tiktok_pixel_id : z.number().nullish(),
    x_user_id : z.number().nullish(),
    x_ad_account_id : z.number().nullish(),
    x_pixel_id : z.number().nullish(),
    currency : z.string().nullish(),
})
export const parse_brand_create = brand_fields
export type parse_brand_create_t = z.infer<typeof parse_brand_create>
export const parse_brand = brand_fields.extend({id : z.number()})
export type brand = z.infer<typeof parse_brand>

export const network_bits : Record<connected_network_tag, network_set> = {
    "nw-facebook" : (1 << 0) as network_set,
    "nw-linkedin" : (1 << 1) as network_set,
    "nw-tiktok"   : (1 << 2) as network_set,
    "nw-x"        : (1 << 3) as network_set,
}
export type network_set = number & { readonly __newtype_network_set : unique symbol }
export const empty_network_set = 0 as network_set
export const network_set_set = (ns : network_set, what : connected_network_tag) : network_set => { return (ns | network_bits[what]) as network_set }

export class Unauthenticated_error extends Error { constructor() { super("Unauthenticated_error") } }

export const Program_state_react_context : R.Context<program_state_box> = R.createContext(undefined as unknown as program_state_box)
export const use_program_state = () => R.useContext(Program_state_react_context).program_state
export const use_authenticated_program_state = () : [program_state, user_state] => {
    const ps = use_program_state()
    const c = ps.current
    switch (c.tag) {
    case Authenticated: return [ps, c]
    case Authenticating: case Unauthenticated: {
        const e = new Unauthenticated_error()
        delete e.stack
        throw e
    } }
}
const _no_attempts_yet : no_attempts_yet = { tag : No_attempts_yet }
export const initial_program_state : program_state = {
    current : { tag : Unauthenticated, last_failure : _no_attempts_yet },
    login_request_counter : generation_counter_init,
    rerender : () => { },
    token_load : () => {
        const s = window.localStorage.getItem(token_localstorage_key)
        return s === null ? null : authn_token_of_string(s)
    },
    token_store : (token : authn_token | null) => {
        const ls = window.localStorage
        if (token !== null) ls.setItem(token_localstorage_key, token as unknown as string)
        else ls.removeItem(token_localstorage_key)
   },
   current_time : () => Date.now(),
   ps_sleep : (delay : number) => new Promise((resolve) => setTimeout(resolve, delay)),
   subscriptions : { generation_counter : generation_counter_init, react_components : new Map() },
}

const token_localstorage_key = "14bf45859c07693f21634bfff57180fc"

export const notify_all = (ps : program_state) => {
    for (const s of ps.subscriptions.react_components.keys()) s()
}
export const update_state = (ps : program_state, f : (us : writable_t<user_state>) => void) => {
    switch (ps.current.tag) {
    case "ps-authenticating": case "ps-unauthenticated": return
    case "ps-authenticated": {
        f(ps.current)
        ps.subscriptions.generation_counter = generation_counter_incr(ps.subscriptions.generation_counter)
        notify_all(ps)
        return
    }
    default: unreachable(ps.current)
    }
}

const ps_subscribe_anything = (ps : program_state) => (change_notification_func : () => void) : (() => void) => {
    ps.subscriptions.react_components.set(change_notification_func, "anything")
    return () => { ps.subscriptions.react_components.delete(change_notification_func) }
}

export const use_connected_networks = () : [connected_network[] | undefined, program_state, user_state] => {
    const [ps, us] = use_authenticated_program_state()
    const subscribe = R.useMemo(() => ps_subscribe_anything(ps), [ps])
    const get_snapshot = R.useCallback(() => ps.subscriptions.generation_counter, [ps, us])
    R.useSyncExternalStore(subscribe, get_snapshot)
    return [us.connected_networks, ps, us]
}
export const use_brands = () : [brand[] | undefined, program_state, user_state] => {
    const [ps, us] = use_authenticated_program_state()
    const subscribe = R.useMemo(() => ps_subscribe_anything(ps), [ps])
    const get_snapshot = R.useCallback(() => ps.subscriptions.generation_counter, [ps, us])
    R.useSyncExternalStore(subscribe, get_snapshot)
    return [us.brands, ps, us]
}

export const use_collapsed_navbar = () : [boolean, (v : boolean) => void] => {
    const [ps, us] = use_authenticated_program_state()
    const subscribe = R.useMemo(() => ps_subscribe_anything(ps), [ps])
    const get_snapshot = R.useCallback(() => ps.subscriptions.generation_counter, [ps, us])
    R.useSyncExternalStore(subscribe, get_snapshot)
    const set_is_collapsed = (v : boolean) => update_state(ps, (us) => us.navbar_is_collapsed = v)
    return [us.navbar_is_collapsed, set_is_collapsed]
}

export const use_saved_audiences = () : [audience_t[] | undefined, program_state, user_state] => {
    const [ps, us] = use_authenticated_program_state()
    const subscribe = R.useMemo(() => ps_subscribe_anything(ps), [ps])
    const get_snapshot = R.useCallback(() => ps.subscriptions.generation_counter, [ps, us])
    R.useSyncExternalStore(subscribe, get_snapshot)
    return [us.saved_audiences, ps, us]
}

export const use_creatives = () : [creative_schema_t[] | undefined, program_state, user_state] => {
    const [ps, us] = use_authenticated_program_state()
    const subscribe = R.useMemo(() => ps_subscribe_anything(ps), [ps])
    const get_snapshot = R.useCallback(() => ps.subscriptions.generation_counter, [ps, us])
    R.useSyncExternalStore(subscribe, get_snapshot)
    return [us.creatives, ps, us]
}

export const use_custom_audiences = () : [custom_audience_t[] | undefined, program_state, user_state] => {
    const [ps, us] = use_authenticated_program_state()
    const subscribe = R.useMemo(() => ps_subscribe_anything(ps), [ps])
    const get_snapshot = R.useCallback(() => ps.subscriptions.generation_counter, [ps, us])
    R.useSyncExternalStore(subscribe, get_snapshot)
    return [us.custom_audiences, ps, us]
}

export const use_overview_stats = () : [overview_stats_t | undefined, program_state, user_state] => {
    const [ps, us] = use_authenticated_program_state()
    const subscribe = R.useMemo(() => ps_subscribe_anything(ps), [ps])
    const get_snapshot = R.useCallback(() => ps.subscriptions.generation_counter, [ps, us])
    R.useSyncExternalStore(subscribe, get_snapshot)
    return [us.overview_stats, ps, us]
}

/******************************************************************************
 ** Definitions involving the state of the entire single-page browser program ]
 ******************************************************************************/

/******************************************************************************
 ** React-specific utilities [
 ******************************************************************************/

export const run_once_on_component_destruction = (f : () => void) => R.useEffect(() => f, [])
export const run_once_on_component_creation = (f : () => void) => R.useEffect(f, [])

/******************************************************************************
 ** React-specific utilities ]
 ******************************************************************************/

/******************************************************************************
 ** HTTP request utilities [
 ******************************************************************************/

const __issue_http_request = async (url : string, init : RequestInit) : Promise<result<Response, TypeError>> => {
    try {
        // send cookies with requests
        const response = await fetch(url, { ...init, credentials: 'include' })
        return [Ok, response]
    } catch (exn : unknown) {
        if (exn instanceof TypeError) return [Err, exn]
        throw exn
    }
}
export const zodparse = <output, def extends z.ZodTypeDef, input>
    (zodparser : z.ZodType<output, def, input>, blob : unknown) : result<output, z.ZodError> =>
{
    try {
        return [Ok, zodparser.parse(blob)]
    } catch (exn) {
        if (exn instanceof z.ZodError) {
            exn.errors.forEach((error) => {
                console.log(`Error in ${error.path.join(' > ')}: ${error.message}`);
              });
            return [Err, exn]
        }
        throw exn
    }
}

export class http_error {
    resp : Response
    constructor(resp : Response) { this.resp = resp }
}

let _global_program_state : program_state | null = null

export const set_global_program_state = (ps : program_state) => {
    _global_program_state = ps
}

/**
 * A 401 on any authenticated call means the session is gone: drop the token and
 * send the person back to log in.
 *
 * Endpoints that authenticate you are the exception. A wrong password also
 * answers 401, and that is not an expired session - it is a failed attempt
 * whose message the login flow is about to display. Running the global handler
 * for those wiped the state the login flow then wrote into, so a mistyped
 * password could clear the form instead of saying what was wrong.
 */
const AUTHENTICATION_ENDPOINTS = [
    "auth/login",
    "auth/password-recovery",
    "auth/reset-password",
]

const is_authentication_request = (url : string) : boolean =>
    AUTHENTICATION_ENDPOINTS.some(endpoint => url.includes(endpoint))

const handle_401_unauthorized = (resp : Response, url : string) => {
    if (resp.status !== 401 || _global_program_state === null) return
    if (is_authentication_request(url)) return

    const ps = _global_program_state
    // the session is over, so drop the token and the derived state
    ps.token_store(null)
    ps.current = { tag : Unauthenticated, last_failure : _no_attempts_yet }
    notify_all(ps)

    const current_path = window.location.pathname
    if (!current_path.startsWith("/login")) {
        window.location.href = `/login?redirect-to=${encodeURIComponent(current_path)}&session_expired=true`
    }
}
export const parse_json_syntax = async (resp : Response) : Promise<result<unknown, SyntaxError>> => {
    // clone the response before reading it, so we can read it again in catch block
    const cloned_resp = resp.clone()
    try {
        const ret = await resp.json()
        return [Ok, ret]
    } catch (exc) {
        if (exc instanceof SyntaxError) {
            console.error("parse_json_syntax: JSON parse failed for URL:", resp.url)
            console.error("parse_json_syntax: Response status:", resp.status, resp.statusText)
            const text = await cloned_resp.text()
            console.error("parse_json_syntax: Response body (first 500 chars):", text.substring(0, 500))
            return [Err, exc]
        }
        throw exc
    }
}
export type possible_http_errors = TypeError | http_error | SyntaxError | z.ZodError
const _issue_json_http_request = async <
    output, def extends z.ZodTypeDef, input>
    (url : string, parseresp : z.ZodType<output, def, input>, request_options : RequestInit)
    : Promise<result<output, possible_http_errors>> =>
{
    let opts = {...request_options}
    if (opts.headers instanceof Headers) throw new Error("FIXME: support dom headers")
    if (opts.headers instanceof Array) throw new Error("FIXME: support alist headers")
    if (opts.headers === undefined) opts.headers = {}
    opts.headers["Accept"] = "application/json"
    const [hok, hval] = await __issue_http_request(url, opts)
    if (!hok) return [Err, hval]
    if (!hval.ok) {
        // a 401 from anything other than logging in means the session expired
        handle_401_unauthorized(hval, url)
        return [Err, new http_error(hval)]
    }
    const [jok, jval] = await parse_json_syntax(hval)
    if (!jok) return [Err, jval]
    const [zok, zval] = zodparse(parseresp, jval)
    if (!zok) {
        console.error("Zod validation failed for:", url)
        console.error("Received data:", jval)
        console.error("Expected schema:", parseresp)
        return [Err, zval]
    }
    return [Ok, zval]
}
const _http_request_with_body = async <
    output, def extends z.ZodTypeDef, input>
    (url : string, reqbody : object | [any] | undefined, parseresp : z.ZodType<output, def, input>, opts : RequestInit, token? : authn_token)
    : Promise<result<output, possible_http_errors>> =>
{
    opts.headers = {}
    if (reqbody !== undefined) {
        opts.headers["Content-Type"] = "application/json"
        opts.body = JSON.stringify(reqbody)
    }
    if (token !== undefined) {
        opts.headers["Authorization"] = `Bearer ${token}`
    }
    return _issue_json_http_request(url, parseresp, opts)
}
export const issue_formdata_post = async <
    output, def extends z.ZodTypeDef, input>
    (url: string, postbody: FormData, parseresp: z.ZodType<output, def, input>, token?: authn_token)
    : Promise<result<output, possible_http_errors>> => {
    const request_options: RequestInit = {
        method: 'POST',
        body: postbody,
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    };

    return _issue_json_http_request(url, parseresp, request_options);
}
export const issue_json_post = async <
    output, def extends z.ZodTypeDef, input>
    (url : string, reqbody : object | [any] | undefined, parseresp : z.ZodType<output, def, input>, token? : authn_token)
    : Promise<result<output, possible_http_errors>> =>
{
    const opts : RequestInit = { method : "POST" }
    return _http_request_with_body(url, reqbody, parseresp, opts, token)
}
export const issue_json_put = async <
    output, def extends z.ZodTypeDef, input>
    (url : string, reqbody : object | [any] | undefined, parseresp : z.ZodType<output, def, input>, token? : authn_token)
    : Promise<result<output, possible_http_errors>> =>
{
    const opts : RequestInit = { method : "PUT" }
    return _http_request_with_body(url, reqbody, parseresp, opts, token)
}
export const issue_json_get = async <
    output, def extends z.ZodTypeDef, input>
    (url : string, parseresp : z.ZodType<output, def, input>, token? : authn_token)
    : Promise<result<output, possible_http_errors>> =>
{
    let opts : RequestInit = { method : "GET" }
    if (token !== undefined) {
        opts.headers = { "Authorization" : `Bearer ${token}` }
    }
    return _issue_json_http_request(url, parseresp, opts)
}
export const issue_json_delete = async <
    output, def extends z.ZodTypeDef, input>
    (url : string, parseresp : z.ZodType<output, def, input>, token? : authn_token)
    : Promise<result<output, possible_http_errors>> =>
{
    let opts : RequestInit = { method : "DELETE" }
    if (token !== undefined) {
        opts.headers = { "Authorization" : `Bearer ${token}` }
    }
    return _issue_json_http_request(url, parseresp, opts)
}


export type raw_fragment = {__eres_raw_fragment : string}
export const rf = (s : string) : raw_fragment => ({__eres_raw_fragment : s})

/**
 * Constructs a URL given a template string pattern. The bare string components
 * of the template string are not escaped, but interpolated expressions are
 * escaped with encodeURIComponent. You can override this behavior and inject
 * an unescaped string by using `rf` which produces a value of type
 * `raw_fragment`.
 *
 * Template literal documentation can be found here:
 * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals}
 */
export const v1api = (strs : TemplateStringsArray, ...vals : any[]) : string => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "/api/v1/"
    const arr : any[] = [baseUrl]
    const len = strs.length
    for (let i = 0; i < len; ++i) {
        const str = strs[i]
        const val = vals[i]
        if (str.length > 0) arr.push(str)
        if (val !== undefined) {
            if (typeof val === 'object' && val.__eres_raw_fragment !== undefined) {
                arr.push(val.__eres_raw_fragment)
            } else {
                arr.push(encodeURIComponent(val))
            }
        }
    }
    return arr.join("")
}

const Completed : unique symbol = Symbol.for("completed")
export const limited_concurrency = async <t extends readonly (() => unknown)[] | []>(
    limit : number, xs : t
) : Promise<{ -readonly [ix in keyof t] : Awaited<ReturnType<t[ix]>> }> =>
{
    let in_flight = 0
    let task_ix = 0
    let status : [Ok, [number, any][]] | [Err, any] | [typeof Completed, undefined] = [Ok, []]
    const it = xs[Symbol.iterator]()
    let resolve : (val : Awaited<t>[]) => void;
    let reject : (reason? : any) => void;
    const ret = new Promise<Awaited<t>[]>((res, rej) => { resolve = res; reject = rej })
    const run_tasks = async () => {
        ++in_flight
        for (;;) {
            const [tag, completions] = status
            if (tag !== Ok) return;
            const itval = it.next();
            if (itval.done === false) {
                const this_task_id = task_ix
                try {
                    const result = await itval.value()
                    const [tag, completions] = status
                    if (tag !== Ok) return;
                    completions.push([this_task_id, result])
                } catch (e) {
                    status = [Err, e]
                    reject(e)
                }
            } else {
                --in_flight
                if (in_flight !== 0) return;
                completions.sort(([tid1, ], [tid2, ]) => tid1 - tid2)
                const results = completions.map(([, r]) => r)
                status = [Completed, undefined]
                resolve(results)
            }
        }
    }
    for (let i = 0; i < limit; ++i) void run_tasks()
    return ret as any
}

export const all_concurrently = async <t extends readonly (() => unknown)[] | []>(xs : t)
    : Promise<{ -readonly [ix in keyof t] : Awaited<ReturnType<t[ix]>> }> =>
{
    const ret = await Promise.all(xs.map((async_func : () => unknown) => async_func()))
    return ret as any
}

export const all_concurrently_rec = async <t extends Record<string, () => unknown>>(xs : t)
    : Promise<{ -readonly [ix in keyof t] : Awaited<ReturnType<t[ix]>> }> =>
{
    const ret : Record<string, any> = {}
    const tasks = []
    for (const k in xs) {
        const v = xs[k] as () => Promise<any>
        if (v === undefined) continue
        tasks.push(v().then(r => ret[k] = r))
    }
    await Promise.all(tasks)
    return ret as any
}

type dlist = {container : unknown, next : dlist, prev : dlist}
const dummy_dlist : dlist = {container : null, next : null as unknown as dlist, prev : null as unknown as dlist}
const dlist_prepend = (where : dlist, node : dlist) => {
    const prev = where.prev
    prev.next = node
    node.prev = prev
    node.next = where
    where.prev = node
}
const dlist_unlink = (where : dlist) => {
    const prev = where.prev
    const next = where.next
    prev.next = next
    next.prev = prev
}

export type semaphore = {__semaphore_count : number, __semaphore_waitq : dlist}
type wait_node = Promise<void> & {__wn_waitq : dlist, __wn_wake : () => void}

export const make_semaphore = (count : number) : semaphore => {
    if (count <= 0 || !Number.isSafeInteger(count)) throw new Error(`invalid non-positive or non-integral semaphore count ${count}`)
    const ret = {__semaphore_count : count, __semaphore_waitq : {...dummy_dlist}}
    const waitq = ret.__semaphore_waitq
    waitq.container = ret
    waitq.next = waitq
    waitq.prev = waitq
    return ret
}
export const wait_for_semaphore = (sem : semaphore) : Promise<void> => {
    if (sem.__semaphore_count == 0) {
        let resolve : (val : void) => void = null as any
        const ret = new Promise((res, rej) => { resolve = res; void rej }) as wait_node
        const __wn_waitq : dlist = {...dummy_dlist, container : ret}
        __wn_waitq.next = __wn_waitq
        __wn_waitq.prev = __wn_waitq
        ret.__wn_waitq = __wn_waitq
        ret.__wn_wake = resolve
        dlist_prepend(sem.__semaphore_waitq, ret.__wn_waitq)
        return ret
    }
    --sem.__semaphore_count
    return Promise.resolve()
}
export const release_semaphore = (sem : semaphore) => {
    const wq = sem.__semaphore_waitq
    const next = wq.next
    if (next !== wq) {
        dlist_unlink(next)
        const wn = next.container as wait_node
        queueMicrotask(wn.__wn_wake)
    } else {
        ++sem.__semaphore_count
    }
}
export const with_semaphore = (sem : semaphore) => async <t,> (f : () => t) : Promise<t> => {
    await wait_for_semaphore(sem)
    let r : t
    try {
        r = await f()
    } finally {
        release_semaphore(sem)
    }
    return r
}
export const all_with_limiter = async <t extends readonly (() => unknown)[] | []>(sem : semaphore, xs : t)
    : Promise<{ -readonly [ix in keyof t] : Awaited<ReturnType<t[ix]>> }> =>
{
    const ret = await Promise.all(xs.map(with_semaphore(sem)))
    return ret as any
}
export const all_with_limiter_rec = async <t extends Record<string, () => unknown>>(sem : semaphore, xs : t)
    : Promise<{ -readonly [ix in keyof t] : Awaited<ReturnType<t[ix]>> }> =>
{
    const ret : Record<string, any> = {}
    const tasks = []
    for (const k in xs) {
        const v = xs[k] as () => Promise<any>
        if (v === undefined) continue
        tasks.push(with_semaphore(sem)(v).then(r => ret[k] = r))
    }
    await Promise.all(tasks)
    return ret as any
}

/******************************************************************************
 ** HTTP request utilities ]
 ******************************************************************************/

/******************************************************************************
 ** Login flow [
 ******************************************************************************/

const login_parser = z.object({ access_token : z.string(), token_type : z.string() })
export async function login_begin_flow(
    ps : program_state, email : string, password : string, redraw_component : () => void,
    navigate : NavigateFunction, redirect_to : string | null)
{
    switch (ps.current.tag) {
    case Authenticated: return /* If the user is already authenticated, don't start the login flow */
    case Authenticating: return /* If the user is currently authenticating, don't start the login flow */
    case Unauthenticated: break /* If the user is unauthenticated, go ahead and continue */
    default: unreachable(ps.current)
    }

    let this_task_request_counter = ps.login_request_counter
    let new_state : authenticating_state = {
        tag : Authenticating,
        login_request_counter_snapshot : this_task_request_counter,
    }
    ps.login_request_counter = generation_counter_incr(ps.login_request_counter)
    ps.current = new_state as current_user_state // This prevents typescript's flow-sensitive type checker from narrowing `ps.current`
    const verify_task_is_still_valid = () : boolean => {
        // It is theoretically possible for another copy of `login_begin_flow`
        // to run while our current request is still outstanding; a curious
        // user might figure out how to call `login_begin_flow` themselves in
        // the browser inspector, programmer error might cause multiple
        // `login_begin_flow`s to be issued concurrently, or we may support
        // cancelling login requests (which would amount to allowing any
        // previously-issued requests to continue and then ignoring their
        // results). To prevent redundant login requests from causing
        // inadvertent destruction of program state, check to make sure, after
        // every await, that the "login request counter" has not changed since
        // this function's last suspension.
        let current = ps.current as current_user_state
        switch (current.tag) {
        case Authenticated: case Unauthenticated: return false
        case Authenticating: return this_task_request_counter === current.login_request_counter_snapshot
        }
    }
    redraw_component()

    const json_payload = { email : email, password : password }
    const [rok, rval] = await issue_json_post(v1api`auth/login/access-token`, json_payload, login_parser)

    if (rok) {
        if (!verify_task_is_still_valid()) return
        const token = authn_token_of_string(rval.access_token)
        ps.token_store(token)
        ps.current = mk_empty_user_state(token)
        login_redirect_to_target_in_query_param(navigate, redirect_to)
    } else {
        await ps.ps_sleep(500)

        const unexpected_error = "Unexpected server error. Please try again later."
        let generic_message = ""
        let password_message = ""
        switch (true) {
        case rval instanceof http_error:
            if (rval.resp.status === 401) {
                password_message = "Unknown email or bad password."
            } else {
                generic_message = unexpected_error // Use a generic error message if the HTTP response wasn't 401 Unauthorized.
            }
            break
        case rval instanceof z.ZodError: // The server returned a JSON blob with an unexpected shape.
        case rval instanceof TypeError: // There was a network error or programmer error when issuing the HTTP request.
        case rval instanceof SyntaxError: // The server returned non-JSON or malformed JSON.
            generic_message = unexpected_error // In all of the above cases, use a generic error message.
            break
        default: unreachable(rval) // Causes compilation to fail if another error case is added.
        }
        ps.current = { tag : Unauthenticated, last_failure : {
            tag : Last_attempt_failed,
            email_message : "",
            password_message : password_message,
            generic_reason : generic_message } }
        redraw_component()
    }
}
export const login_redirect_to_target_in_query_param = (navigate : NavigateFunction, redirect_to : string | null) => {
    //const _target_url = router.query["redirect-to"]
    const target = redirect_to === null ? "/dashboard" : redirect_to
    navigate(target)
}
export const login_handle_navigating_away_from_login_page = (ps : program_state) => {
    const reset_state = () => ps.current = { tag : Unauthenticated, last_failure : _no_attempts_yet }
    switch (ps.current.tag) {
    case Unauthenticated:
        switch (ps.current.last_failure.tag) {
        case Last_attempt_failed: reset_state(); break /* If the user is unauthenticated and failed logging in, reset */
        case No_attempts_yet: break /* If the user is unauthenticated but hasn't tried logging in, do nothing */
        default: unreachable(ps.current.last_failure)
        }
        break
    case Authenticating: reset_state(); break /* If the user is currently authenticating, reset */
    case Authenticated: break /* If the user is already logged in, don't do anything */
    default: unreachable(ps.current)
    }
}
export const login_empty_failure : last_attempt_failed = {
    tag : Last_attempt_failed,
    email_message : "",
    password_message : "",
    generic_reason : "",
}

/******************************************************************************
 ** Login flow ]
 ******************************************************************************/

/******************************************************************************
 ** Connected network fetch [
 ******************************************************************************/

const num_to_string = (x : number) : string => x.toString()
const facebook_user_response = z.array(z.object({
    id : z.number(),
    facebook_id : z.string(),
    name : z.string().nullish(),
    username : z.string().nullish(),
    _created : z.string().datetime({offset : true}),
}))
const linkedin_user_response = z.array(z.object({
    id : z.number(),
    linkedin_id : z.string(),
    first_name : z.string().nullish(),
    last_name : z.string().nullish(),
    username : z.string().nullish(),
    _created : z.string().datetime({offset : true}),
}))
export const tiktok_single_user_response = z.object({
    id : z.number(),
    tiktok_id : z.string(),
    displayname : z.string(),
    email : z.string(),
    avatar_url : z.string().nullish(),
    _created : z.string().datetime({offset : true}),
})
export const tiktok_user_response = z.array(tiktok_single_user_response)
export type tiktok_single_user_response_t = z.infer<typeof tiktok_single_user_response>

const x_user_response = z.array(z.object({
    id : z.number(),
    x_id : z.string(),
    screen_name : z.string(),
    display_name : z.string(),
    email : z.string().nullish(),
}))

const fetch_fb_networks = async (ustate : user_state, acc : connected_network[]) : Promise<result<null, {}>> => {
    const [rok, rval] = await issue_json_get(v1api`social/facebook/users`, facebook_user_response, ustate.token)
    if (!rok) {
        console.log("XXXih: not ok", rval)
        return [Err, {}]
    }
    for (const e of rval) {
        const name = e.name ? e.name : "XXX no name???"
        const eres_dbid = num_to_string(e.id) as connected_network_dbid
        acc.push({
            network_tag : "nw-facebook",
            eres_dbid : eres_dbid,
            name : name,
            network_account_id : e.facebook_id as network_account_foreign_id,
            creation_time : Date.parse(e._created),
        })
    }
    return [Ok, null]
}
const fetch_li_networks = async (ustate : user_state, acc : connected_network[]) : Promise<result<null, {}>> => {
    const [rok, rval] = await issue_json_get(v1api`social/linkedin/users`, linkedin_user_response, ustate.token)
    if (!rok) {
        console.log("XXXih: not ok", rval)
        return [Err, {}]
    }
    for (const e of rval) {
        const name_parts = []
        if (e.first_name && e.last_name) {
            name_parts.push(e.first_name)
            name_parts.push(" ")
            name_parts.push(e.last_name)
        } else if (e.first_name) {
            name_parts.push(e.first_name)
        } else if (e.last_name) {
            name_parts.push(e.last_name)
        }
        if (e.username) {
            const surround_username = name_parts.length > 0
            if (surround_username) name_parts.push(" (")
            name_parts.push(e.username)
            if (surround_username) name_parts.push(")")
        }
        if (name_parts.length == 0) name_parts.push(e.linkedin_id)
        acc.push({
            network_tag : "nw-linkedin",
            eres_dbid : num_to_string(e.id) as connected_network_dbid,
            name : name_parts.join(""),
            network_account_id : e.linkedin_id as network_account_foreign_id,
            creation_time : Date.parse(e._created),
        })
    }
    return [Ok, null]
}
const fetch_tt_networks = async (ustate : user_state, acc : connected_network[]) : Promise<result<null, {}>> => {
    const [rok, rval] = await issue_json_get(v1api`social/tiktok/users`, tiktok_user_response, ustate.token)
    if (!rok) {
        console.log("XXXih: not ok", rval)
        return [Err, {}]
    }
    for (const e of rval) {
        const eres_dbid = num_to_string(e.id) as connected_network_dbid
        acc.push({
            network_tag : "nw-tiktok",
            eres_dbid : eres_dbid,
            name : e.displayname,
            network_account_id : e.tiktok_id as network_account_foreign_id,
            creation_time : Date.parse(e._created),
        })
    }
    return [Ok, null]
}

const fetch_x_networks = async (ustate : user_state, acc : connected_network[]) : Promise<result<null, {}>> => {
    const [rok, rval] = await issue_json_get(v1api`social/x/users`, x_user_response, ustate.token)
    if (!rok) {
        console.log("XXXih: not ok", rval)
        return [Err, {}]
    }
    for (const e of rval) {
        const eres_dbid = num_to_string(e.id) as connected_network_dbid
        const name = `${e.display_name} (@${e.screen_name})`
        acc.push({
            network_tag : "nw-x",
            eres_dbid : eres_dbid,
            name : name,
            network_account_id : e.x_id as network_account_foreign_id,
            creation_time : Date.now(),
        })
    }
    return [Ok, null]
}

const fetch_connected_networks = async (ustate : user_state) : Promise<result<connected_network[], {}>> => {
    let ret : connected_network[] = []
    const fb_task = fetch_fb_networks(ustate, ret)
    const li_task = fetch_li_networks(ustate, ret)
    const tt_task = fetch_tt_networks(ustate, ret)
    const x_task = fetch_x_networks(ustate, ret)
    const results = await Promise.all([fb_task, li_task, tt_task, x_task])
    for (const [ok, _] of results) {
        if (!ok) {
            console.log("XXXih: TODO: error handling!!!!!!")
            return [Err, {}]
        }
    }
    return [Ok, ret]
}

const parse_brands = z.array(parse_brand)

const fetch_brands = async (us : user_state) : Promise<result<brand[], {}>> => {
    const [rok, rval] = await issue_json_get(v1api`brands`, parse_brands, us.token)
    if (!rok) {
        console.log("XXXih: brands: not ok", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

const fetch_saved_audiences = async (us : user_state) : Promise<result<audience_t[], {}>> => {
    const [rok, rval] = await issue_json_get(v1api`audiences`, audiences_schema, us.token)
    if (!rok) {
        console.log("XXXih: saved audiences: not ok", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

const fetch_creative_bundles = async (us : user_state) : Promise<result<creative_schema_t[], {}>> => {
    const [rok, rval] = await issue_json_get(v1api`smart-campaign/creatives/`, creatives_schema, us.token)
    if (!rok) {
        console.log("XXXih: fetch_creative_bundles: not ok", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

const fetch_custom_audiences = async (us : user_state) : Promise<result<custom_audience_t[], {}>> => {
    const [rok, rval] = await issue_json_get(v1api`audiencesv2/custom-audiences`, custom_audiences_schema, us.token)
    if (!rok) {
        console.log("XXXih: fetch_custom_audiences: not ok", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

const fetch_overview_stats = async (us : user_state) : Promise<result<overview_stats_t, {}>> => {
    const [rok, rval] = await issue_json_get(v1api`insight/`, overview_stats_schema, us.token)
    if (!rok) {
        console.log("XXXih: fetch_overview_stats: not ok", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

const seconds = (s : number) : number => s * 1000

const dumb_poll = async <r extends user_state[keyof user_state] , e>(ps : program_state, us : user_state,
    field_last_update : fields_with_type<number, user_state>,
    field_fetch_task : fields_with_type<{should_abort : boolean} | null, user_state>,
    action : (us : user_state) => Promise<result<r, e>>,
    set : (us : writable_t<user_state>, r : r) => void,
) => {
    const now = ps.current_time()
    if (now - us[field_last_update] > seconds(10)) {
        if (us[field_fetch_task] !== null) return
        const handle = { should_abort : false }
        us[field_fetch_task] = handle
        notify_all(ps)
        const [ok, resp] = await action(us)
        // await ps.ps_sleep(seconds(1))
        update_state(ps, (us : writable_t<user_state>) => {
            if (!handle.should_abort) {
                if (ok) {
                    us[field_last_update] = ps.current_time()
                    set(us, resp)
                } else {
                    console.log("XXXih: failure!", resp)
                }
            }
            us[field_fetch_task] = null
        })
    }
}

export const poll_connected_networks = (ps : program_state, us : user_state) => dumb_poll(ps, us,
    "connected_networks_last_update", "connected_networks_current_fetch_task",
    fetch_connected_networks, (us, r) => { us.connected_networks = r })

export const poll_connected_brands = (ps : program_state, us : user_state) => dumb_poll(ps, us,
    "brands_last_update", "brands_current_fetch_task",
    fetch_brands, (us, r) => { us.brands = r })

export const poll_saved_audiences = (ps : program_state, us : user_state) => dumb_poll(ps, us,
    "saved_audiences_last_update", "saved_audiences_current_fetch_task",
    fetch_saved_audiences, (us, r) => { us.saved_audiences = r })

export const poll_creatives = (ps : program_state, us : user_state) => dumb_poll(ps, us,
    "creatives_last_update", "creatives_current_fetch_task",
    fetch_creative_bundles, (us, r) => { us.creatives = r })

export const poll_custom_audiences = (ps : program_state, us : user_state) => dumb_poll(ps, us,
    "custom_audiences_last_update", "custom_audiences_current_fetch_task",
    fetch_custom_audiences, (us, r) => { us.custom_audiences = r })

export const poll_overview_stats = (ps : program_state, us : user_state) => dumb_poll(ps, us,
    "overview_stats_last_update", "overview_stats_current_fetch_task",
    fetch_overview_stats, (us, r) => { us.overview_stats = r })
/******************************************************************************
 ** Connected network fetch [
 ******************************************************************************/


/******************************************************************************
 ** Facebook asset fetch [
 ******************************************************************************/

export type fbid = string & { readonly __tag : unique symbol }
export const z_fbid = z.string as unknown as () => z.ZodType<fbid, z.ZodTypeDef, fbid>

export type facebook_page_parse_t = z.infer<typeof facebook_page_parse>
export const facebook_page_parse = z.object({
    facebook_id : z_fbid(),
    name : z.string(),
    id : z.number(),
})
export const facebook_pages_parse = z.array(facebook_page_parse)

export type facebook_ad_account_parse_t = z.infer<typeof facebook_ad_account_parse>
export const facebook_ad_account_parse = z.object({
    facebook_id : z_fbid(),
    name : z.string(),
    id : z.number(),
})
export const facebook_ad_accounts_parse = z.array(facebook_ad_account_parse)

export type facebook_business_manager_parse_t = z.infer<typeof facebook_business_manager_parse>
export const facebook_business_manager_parse = z.object({
    facebook_id : z_fbid(),
    name : z.string(),
    id : z.number(),
})
export const facebook_business_managers_parse = z.array(facebook_business_manager_parse)

export type facebook_instagram_parse_t = z.infer<typeof facebook_instagram_parse>
export const facebook_instagram_parse = z.object({
    facebook_id : z_fbid(),
    username : z.string(),
    id : z.number(),
})
export const facebook_instagrams_parse = z.array(facebook_instagram_parse)

export type facebook_pixel_parse_t = z.infer<typeof facebook_pixel_parse>
export const facebook_pixel_parse = z.object({
    facebook_id : z_fbid(),
    name : z.string(),
    id : z.number(),
})
export const facebook_pixels_parse = z.array(facebook_pixel_parse)

export type wrap_fields_in_http_promise<t> = {
    [k in keyof t] : () => Promise<result<(t[k])[], possible_http_errors>>
}

export type facebook__asset_types_record = {
    fb_page : facebook_page_parse_t,
    fb_adaccount : facebook_ad_account_parse_t,
    fb_businessmanager : facebook_business_manager_parse_t,
    fb_instagram : facebook_instagram_parse_t,
    fb_pixel : facebook_pixel_parse_t,
}

export type facebook__reqs_for_assets = wrap_fields_in_http_promise<facebook__asset_types_record>

export const facebook__requests_for_all_assets = (eres_fb_dbid : string, token : authn_token) : facebook__reqs_for_assets => {
    const pages_task = () => issue_json_get(v1api`social/facebook/${eres_fb_dbid}/pages`, facebook_pages_parse, token)
    const adact_task = () => issue_json_get(v1api`social/facebook/${eres_fb_dbid}/ad-accounts`, facebook_ad_accounts_parse, token)
    const bmgrs_task = () => issue_json_get(v1api`social/facebook/${eres_fb_dbid}/business-managers`, facebook_business_managers_parse, token)
    const igact_task = () => issue_json_get(v1api`social/facebook/${eres_fb_dbid}/instagrams`, facebook_instagrams_parse, token)
    const pixel_task = () => issue_json_get(v1api`social/facebook/${eres_fb_dbid}/pixels`, facebook_pixels_parse, token)
    return {
        fb_page : pages_task,
        fb_adaccount : adact_task,
        fb_businessmanager : bmgrs_task,
        fb_instagram : igact_task,
        fb_pixel : pixel_task,
    }
}

/******************************************************************************
 ** Facebook asset fetch ]
 ******************************************************************************/

/******************************************************************************
 ** Linkedin asset fetch [
 ******************************************************************************/

export type liid = string & { readonly __tag : unique symbol }
export const z_liid = (() => z.union([z.string(), z.number()]).pipe(z.coerce.string())) as unknown as () => z.ZodType<liid, z.ZodTypeDef, liid>

export const linkedin__ad_account_parse = z.object({
    id : z.number(),
    linkedin_id : z_liid(),
    name : z.string(),
})
export const linkedin__ad_accounts_parse = z.array(linkedin__ad_account_parse)
export type linkedin__ad_account_t = z.infer<typeof linkedin__ad_account_parse>

export const linkedin__organization_parse = z.object({
    id : z.number(),
    linkedin_id : z_liid(),
    name : z.string().nullish(),
})
export const linkedin__organizations_parse = z.array(linkedin__organization_parse)
export type linkedin__organization_t = z.infer<typeof linkedin__organization_parse>

export const linkedin__insight_tag_parse = z.object({
    id : z.number(),
    linkedin_id : z_liid(),
})
export const linkedin__insight_tags_parse = z.array(linkedin__insight_tag_parse)
export type linkedin__insight_tag_t = z.infer<typeof linkedin__insight_tag_parse>

export type linkedin__asset_types_record = {
    li_adaccount : linkedin__ad_account_t,
    li_organization : linkedin__organization_t,
    li_insighttag : linkedin__insight_tag_t,
}
export type linkedin__reqs_for_assets = wrap_fields_in_http_promise<linkedin__asset_types_record>

export const linkedin__requests_for_all_assets = (eres_li_dbid : string, token : authn_token) : linkedin__reqs_for_assets => {
    const adact_task = () => issue_json_get(v1api`social/linkedin/${eres_li_dbid}/ad-accounts`, linkedin__ad_accounts_parse, token)
    const org_task = () => issue_json_get(v1api`social/linkedin/${eres_li_dbid}/organizations`, linkedin__organizations_parse, token)
    const itag_task = () => issue_json_get(v1api`social/linkedin/${eres_li_dbid}/insight-tags`, linkedin__insight_tags_parse, token)
    return {
        li_adaccount : adact_task,
        li_organization : org_task,
        li_insighttag : itag_task,
    }
}

/******************************************************************************
 ** Linkedin asset fetch ]
 ******************************************************************************/

/******************************************************************************
 ** Tiktok asset fetch [
 ******************************************************************************/

export type ttid = string & { readonly __tag : unique symbol }
export const z_ttid = (() => z.union([z.string(), z.number()]).pipe(z.coerce.string())) as unknown as () => z.ZodType<ttid, z.ZodTypeDef, ttid>

export const tiktok__ad_account_parse = z.object({
    id : z.number(),
    advertiser_id : z_ttid(),
    name : z.string(),
})
export const tiktok__ad_accounts_parse = z.array(tiktok__ad_account_parse)
export type tiktok__ad_account_parse_t = z.infer<typeof tiktok__ad_account_parse>

export const tiktok__business_center_parse = z.object({
    id : z.number(),
    bc_id : z_ttid(),
    name : z.string(),
})
export const tiktok__business_centers_parse = z.array(tiktok__business_center_parse)
export type tiktok__business_center_parse_t = z.infer<typeof tiktok__business_center_parse>

export const tiktok__identity_parse = z.object({
    id : z.number(),
    identity_id : z_ttid(),
    display_name : z.string(),
})
export const tiktok__identities_parse = z.array(tiktok__identity_parse)
export type tiktok__identity_parse_t = z.infer<typeof tiktok__identity_parse>

export const tiktok__pixel_parse = z.object({
    id : z.number(),
    pixel_id : z_ttid(),
    pixel_name : z.string(),
})
export const tiktok__pixels_parse = z.array(tiktok__pixel_parse)
export type tiktok__pixel_parse_t = z.infer<typeof tiktok__pixel_parse>

export type tiktok_assets_record = {
    tt_ad_accounts : tiktok__ad_account_parse_t,
    tt_business_centers : tiktok__business_center_parse_t,
    tt_identities : tiktok__identity_parse_t,
    tt_pixels : tiktok__pixel_parse_t,
}

export type tiktok__reqs_for_assets = wrap_fields_in_http_promise<tiktok_assets_record>

export const tiktok__requests_for_all_assets = (eres_tt_dbid : string, token : authn_token) : tiktok__reqs_for_assets => {
    const adact_task = () => issue_json_get(v1api`social/tiktok/${eres_tt_dbid}/ad-accounts`, tiktok__ad_accounts_parse, token)
    const bctrs_task = () => issue_json_get(v1api`social/tiktok/${eres_tt_dbid}/business-centers`, tiktok__business_centers_parse, token)
    const ident_task = () => issue_json_get(v1api`social/tiktok/${eres_tt_dbid}/identities`, tiktok__identities_parse, token)
    const pixel_task = () => issue_json_get(v1api`social/tiktok/${eres_tt_dbid}/pixels`, tiktok__pixels_parse, token)
    return {
        tt_ad_accounts : adact_task,
        tt_business_centers : bctrs_task,
        tt_identities : ident_task,
        tt_pixels : pixel_task,
    }
}

/******************************************************************************
 ** Tiktok asset fetch ]
 ******************************************************************************/

/******************************************************************************
 ** X (Twitter) asset fetch [
 ******************************************************************************/

export type { x_user_t, x_ad_account_t, x_pixel_t } from "./resource/types"
export { x_ad_accounts_schema, x_pixels_schema } from "./resource/types"

/******************************************************************************
 ** X asset fetch ]
 ******************************************************************************/


/******************************************************************************
 ** Connected network fetch ]
 ******************************************************************************/


// getter
export const get_brand = (brands: brand_t[], id: number | null) => {
    return brands && brands.find((item) => item.id == id)
}

export const get_creative = (creatives: creative_schema_t[], id: number | null) => {
    return creatives && creatives.find((item) => item.id == id)
}

export const get_audience = (audiences : audience_t[], id: number | null) => {
    return audiences && audiences.find((item) => item.id == id)
}

/******************************************************************************
 ** Dashboard API fetch [
 ******************************************************************************/

export const fetch_dashboard_overview = async (us : user_state, days? : number) : Promise<result<dashboard_overview_t, {}>> => {
    const endpoint = days ? v1api`dashboard/overview?days=${days}` : v1api`dashboard/overview`
    const [rok, rval] = await issue_json_get(endpoint, dashboard_overview_schema, us.token)
    if (!rok) {
        console.log("dashboard_overview: not ok", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

export const fetch_network_health = async (us : user_state, brand_id? : number) : Promise<result<dashboard_network_health_t, {}>> => {
    const endpoint = brand_id ? v1api`dashboard/network-health?brand_id=${brand_id}` : v1api`dashboard/network-health`
    const [rok, rval] = await issue_json_get(endpoint, dashboard_network_health_schema, us.token)
    if (!rok) {
        console.log("network_health: not ok", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

export const fetch_top_performing = async (us : user_state, metric? : string, limit? : number, days? : number) : Promise<result<dashboard_top_performing_t, {}>> => {
    const params = new URLSearchParams()
    if (metric) params.append('metric', metric)
    if (limit) params.append('limit', String(limit))
    if (days) params.append('days', String(days))
    const query_string = params.toString()
    const endpoint = query_string ? v1api`dashboard/top-performing?${rf(query_string)}` : v1api`dashboard/top-performing`
    const [rok, rval] = await issue_json_get(endpoint, dashboard_top_performing_schema, us.token)
    if (!rok) {
        console.log("top_performing: not ok", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

/******************************************************************************
 ** Dashboard API fetch ]
 ******************************************************************************/

/******************************************************************************
 ** Billing and Subscription API [
 ******************************************************************************/

export const fetch_all_plans = async (us : user_state) : Promise<result<import("./resource/types").usage_tier_t[], {}>> => {
    const [rok, rval] = await issue_json_get(v1api`transaction/get-all-plans`, all_plans_schema, us.token)
    if (!rok) {
        console.log("fetch_all_plans: not ok", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

export const fetch_user_subscription = async (us : user_state) : Promise<result<import("./resource/types").user_subscription_t, {}>> => {
    const [rok, rval] = await issue_json_get(v1api`transaction/user-subscription`, user_subscription_schema, us.token)
    if (!rok) {
        console.log("fetch_user_subscription: not ok", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

export const fetch_campaign_eligibility = async (us : user_state) : Promise<result<import("./resource/types").campaign_eligibility_t, {}>> => {
    const [rok, rval] = await issue_json_get(v1api`smart-campaign/is-eligible-to-create-new-campaign/`, campaign_eligibility_schema, us.token)
    if (!rok) {
        console.log("fetch_campaign_eligibility: not ok", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

export const fetch_checkout_session_status = async (session_id : string, us : user_state) : Promise<result<{status : string, payment_status : string, customer_email? : string}, {}>> => {
    const endpoint = v1api`transaction/checkout-session-status?session_id=${session_id}`
    const schema = z.object({
        status: z.string(),
        payment_status: z.string(),
        customer_email: z.string().optional()
    })
    const [rok, rval] = await issue_json_get(endpoint, schema, us.token)
    if (!rok) {
        console.error("fetch_checkout_session_status: failed", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

export const create_checkout_session = async (us : user_state, price_id : string) : Promise<result<{uri : string}, {}>> => {
    const endpoint = v1api`transaction/create-checkout-session?redirect=false`

    // pass the frontend url for Stripe to redirect back to
    const success_url = `${window.location.origin}/account/billing/result?success=true&session_id={CHECKOUT_SESSION_ID}`
    const cancel_url = `${window.location.origin}/account`

    const [rok, rval] = await issue_json_post(
        endpoint,
        {
            price_id,
            success_url,
            cancel_url
        },
        z.object({uri : z.string()}),
        us.token
    )
    if (!rok) {
        console.error("create_checkout_session: failed", rval)
        return [Err, {}]
    }
    return [Ok, rval]
}

export const get_customer_portal = async (us : user_state, return_url? : string) : Promise<result<{url : string}, {}>> => {
    const portal_return_url = return_url || `${window.location.origin}/account`
    // v1api auto-encodes interpolated values, so we pass the raw URL directly
    const endpoint = v1api`transaction/customer-portal?redirect=false&return_url=${portal_return_url}`

    const [rok, rval] = await issue_json_get(
        endpoint,
        z.object({type: z.string(), uri : z.string()}),
        us.token
    )
    if (!rok) {
        console.error("get_customer_portal: failed", rval)
        if (rval instanceof http_error) {
            const text = await rval.resp.text()
            console.error("get_customer_portal: response status:", rval.resp.status)
            console.error("get_customer_portal: response body:", text)
        }
        return [Err, {}]
    }
    return [Ok, {url: rval.uri}]
}

/******************************************************************************
 ** Billing and Subscription API ]
 ******************************************************************************/

/******************************************************************************
 ** Billing helper functions [
 ******************************************************************************/

export const check_campaign_eligibility_with_prompt = async (us : user_state) : Promise<boolean> => {
    const [ok, data] = await fetch_campaign_eligibility(us)
    if (!ok) {
        console.error("Failed to check campaign eligibility")
        return true
    }

    if (!data.is_eligible) {
        const message = data.max_campaigns
            ? `Campaign Limit Reached\n\nYou've reached your limit of ${data.max_campaigns} active campaigns. Please upgrade your plan to create more campaigns.`
            : "Cannot create campaign at this time."

        const should_upgrade = window.confirm(message + "\n\nWould you like to view upgrade options?")
        if (should_upgrade) {
            window.location.href = "/plans"
        }
        return false
    }

    return true
}

/******************************************************************************
 ** Billing helper functions ]
 ******************************************************************************/

/******************************************************************************
 ** Usage stats helper [
 ******************************************************************************/

export type usage_stats_t = {
    brand_count : number
    campaign_count : number
    team_member_count : number
}

export const get_usage_stats = (us : user_state) : usage_stats_t => {
    return {
        brand_count: us.brands?.length || 0,
        campaign_count: us.creatives?.length || 0,
        team_member_count: 1, // TODO: Update when team API is available
    }
}

/******************************************************************************
 ** Usage stats helper ]
 ******************************************************************************/
